import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  Building2,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Link,
  Image,
  RefreshCw,
  GripVertical
} from 'lucide-react';
import { API_URL as API } from '../../config/api';

const PartnersManager = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    logo_url: '',
    website_url: '',
    order: 0
  });

  const token = localStorage.getItem('session_token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/partners`, {
        headers,
        withCredentials: true
      });
      setPartners(response.data.partners || []);
    } catch (error) {
      console.error('Error loading partners:', error);
      toast.error('Erreur lors du chargement des partenaires');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.logo_url) {
      toast.error('Nom et logo requis');
      return;
    }

    try {
      if (editingPartner) {
        await axios.put(`${API}/admin/partners/${editingPartner.partner_id}`, formData, {
          headers,
          withCredentials: true
        });
        toast.success('Partenaire mis à jour');
      } else {
        await axios.post(`${API}/admin/partners`, formData, {
          headers,
          withCredentials: true
        });
        toast.success('Partenaire ajouté');
      }

      setShowForm(false);
      setEditingPartner(null);
      resetForm();
      loadPartners();
    } catch (error) {
      console.error('Error saving partner:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      logo_url: partner.logo_url,
      website_url: partner.website_url || '',
      order: partner.order || 0
    });
    setShowForm(true);
  };

  const handleDelete = async (partnerId) => {
    if (!window.confirm('Supprimer ce partenaire ?')) return;

    try {
      await axios.delete(`${API}/admin/partners/${partnerId}`, {
        headers,
        withCredentials: true
      });
      toast.success('Partenaire supprimé');
      loadPartners();
    } catch (error) {
      console.error('Error deleting partner:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      logo_url: '',
      website_url: '',
      order: partners.length
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Gestion des Partenaires
          </h2>
          <p className="text-gray-500 text-sm">{partners.length} partenaire(s)</p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-primary">
          <Plus className="w-4 h-4 mr-1" /> Ajouter
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="bg-white rounded-2xl shadow-sm p-6 border-2 border-primary/20">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold">
              {editingPartner ? 'Modifier le partenaire' : 'Nouveau partenaire'}
            </h3>
            <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); setEditingPartner(null); }}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nom du partenaire *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: EDENLIFE International"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Ordre d'affichage</label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  min="0"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">URL du logo *</label>
              <div className="relative">
                <Image className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={formData.logo_url}
                  onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                  placeholder="https://example.com/logo.png"
                  className="pl-10"
                  required
                />
              </div>
              {formData.logo_url && (
                <div className="mt-2 p-2 bg-gray-50 rounded-lg inline-block">
                  <img 
                    src={formData.logo_url} 
                    alt="Preview" 
                    className="h-12 object-contain"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Site web (optionnel)</label>
              <div className="relative">
                <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={formData.website_url}
                  onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                  placeholder="https://example.com"
                  className="pl-10"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingPartner(null); }}>
                Annuler
              </Button>
              <Button type="submit" className="bg-primary">
                <Save className="w-4 h-4 mr-1" />
                {editingPartner ? 'Mettre à jour' : 'Ajouter'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Partners List */}
      <div className="bg-white rounded-2xl shadow-sm">
        {partners.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Aucun partenaire</p>
            <Button onClick={() => setShowForm(true)} className="mt-4">
              <Plus className="w-4 h-4 mr-1" /> Ajouter le premier partenaire
            </Button>
          </div>
        ) : (
          <div className="divide-y">
            {partners.map((partner, index) => (
              <div key={partner.partner_id} className="p-4 flex items-center gap-4 hover:bg-gray-50">
                <div className="text-gray-400">
                  <GripVertical className="w-5 h-5" />
                </div>
                <div className="w-20 h-14 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                  <img 
                    src={partner.logo_url} 
                    alt={partner.name}
                    className="max-w-full max-h-full object-contain p-1"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z"/></svg>';
                    }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold">{partner.name}</h3>
                  {partner.website_url && (
                    <a 
                      href={partner.website_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <Link className="w-3 h-3" />
                      {partner.website_url}
                    </a>
                  )}
                </div>
                <div className="text-sm text-gray-400">
                  #{partner.order || index + 1}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(partner)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleDelete(partner.partner_id)}
                    className="text-red-500 hover:text-red-700 hover:border-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview Banner */}
      {partners.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h3 className="font-bold mb-4">Aperçu du bandeau partenaires</h3>
          <div className="bg-gray-100 rounded-lg p-4 overflow-hidden">
            <div className="flex items-center justify-center gap-8 flex-wrap">
              {partners.slice(0, 6).map((partner) => (
                <img 
                  key={partner.partner_id}
                  src={partner.logo_url} 
                  alt={partner.name}
                  className="h-10 object-contain grayscale hover:grayscale-0 transition-all"
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default PartnersManager;
